/**
 * „Wirtschaftlichkeit in der Probezeit" im Personenfenster (Spezifikation
 * 2026-09-16, R3/R7). Holt die Person selbst, damit beide Fenster
 * (Onboarding und Mitarbeiter) den Block ohne eigene Datenlogik einhängen können.
 *
 * Jede Zahl und jeder zusammengesetzte Satz kommt aus `lib/probezeit-anzeige.ts`: Rundung,
 * Zahlenform, Vorzeichen, Farben, Kopfzeile, Kostenzeile und die Sätze der Kästen. Hier stehen
 * die Anordnung und die festen Beschriftungen (Überschrift, Spaltenköpfe, Zeilennamen).
 */
import { useProbezeitPerson } from "../../hooks/useProbezeitPerson.js";
import type { ProbezeitErgebnisMitReihe, ProbezeitPerson } from "../../lib/api.js";
import {
  AMPEL_FARBE,
  AMPEL_TEXTFARBE,
  ampelPilleText,
  diffAusAnzeige,
  diffText,
  euroText,
  keineDatenText,
  kopfzeileText,
  kostenzeileText,
  nichtGerechnetSatz,
  probezeitEndeText,
  probezeitFehlerText,
  rundeStunden,
  rundeUmsatz,
  satzText,
  standZeile,
  startetAmText,
  stundenText,
  umsatzDiffFarbe,
  vkDiffAusAnzeige,
  vkText,
  zusatzStunden,
  zusatzVk,
  zusatzVollkosten,
} from "../../lib/probezeit-anzeige.js";
import { KpiInfo } from "../ui/KpiInfo.js";
import { WochenBalken } from "./WochenBalken.js";

const KASTEN = { background: "var(--bg-card)", border: "1px solid var(--border)" };

/**
 * Erschöpfungsprüfung für den `switch` unten: Kommt im Backend ein Status dazu, den er nicht
 * kennt, meldet `tsc` den Fehler, statt dass ein falscher Satz erscheint. Zur Laufzeit zeigt ein
 * unbekannter Status nichts.
 */
function ausgeschlossen(_status: never): null {
  return null;
}

export function ProbezeitWirtschaftBlock({ personioId }: { personioId: number }) {
  const { data, isLoading, isError, error } = useProbezeitPerson(personioId);

  if (isError) {
    // Welche Fehler stumm bleiben, entscheidet `probezeitFehlerText`; jeder andere bekommt einen
    // kurzen Satz, damit er nicht unbemerkt bleibt.
    const fehlerText = probezeitFehlerText(error.status);
    if (fehlerText === null) return null;
    return (
      <div className="rounded-lg p-4" style={KASTEN}>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {fehlerText}
        </p>
      </div>
    );
  }
  // Beim Laden bleibt es leer: Die meisten Personen sind nicht in der Probezeit, ihre Anfrage endet
  // gleich mit 404. Ein Platzhalter würde dort kurz aufblitzen und alles darunter zweimal
  // verschieben (gemessen: 74 px nach unten, dann wieder hoch).
  if (isLoading || !data) return null;

  const wirtschaft = data.wirtschaft;
  switch (wirtschaft.status) {
    case "nichtGerechnet":
      // Außerhalb des Kundensegments zeigt die Spezifikation gar keinen Kasten (R7).
      if (wirtschaft.grund === "segment") return null;
      return (
        <Kurzkasten
          satz={nichtGerechnetSatz(wirtschaft.grund)}
          zusatz={probezeitEndeText(data.probezeitEnde)}
        />
      );
    case "kuenftig":
      return <Kurzkasten satz={startetAmText(data.eintritt)} />;
    case "keineDaten":
      return <Kurzkasten satz={keineDatenText()} />;
    case "gerechnet":
      return <GerechneterKasten person={data} ergebnis={wirtschaft.ergebnis} />;
    default:
      return ausgeschlossen(wirtschaft);
  }
}

/** Kasten ohne Zahlen: Überschrift, ein Satz, auf Wunsch eine kleine Zeile darunter. */
function Kurzkasten({ satz, zusatz }: { satz: string; zusatz?: string }) {
  return (
    <div className="rounded-lg p-4" style={KASTEN}>
      <h4 className="text-sm font-semibold mb-1" style={{ color: "var(--text-primary)" }}>
        Wirtschaftlichkeit in der Probezeit
      </h4>
      <p className="text-sm" style={{ color: "var(--text-muted)" }}>
        {satz}
      </p>
      {zusatz && (
        <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
          {zusatz}
        </p>
      )}
    </div>
  );
}

function GerechneterKasten({
  person,
  ergebnis: e,
}: {
  person: ProbezeitPerson;
  ergebnis: ProbezeitErgebnisMitReihe;
}) {
  const stufenwerte = person.stufenwerte;
  const umsatzDiff = diffAusAnzeige(e.istUmsatz, e.zielUmsatz, rundeUmsatz);
  const stundenDiff = diffAusAnzeige(e.istStunden, e.zielStunden, rundeStunden);
  const vkDiff = vkDiffAusAnzeige(e.vkIst, e.vkZiel);
  const stand = standZeile(e.leistungenBis);

  return (
    <div className="rounded-lg p-4" style={KASTEN}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h4
            className="text-sm font-semibold flex items-center gap-1"
            style={{ color: "var(--text-primary)" }}
          >
            Wirtschaftlichkeit in der Probezeit
            <KpiInfo schluessel="probezeitAmpel" label="Probezeit-Ampel" />
          </h4>
          <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
            {kopfzeileText(e, person)} <KpiInfo schluessel="probezeitWoche" label="Woche" />
          </p>
        </div>
        <span
          className="px-2.5 py-1 rounded text-sm font-semibold whitespace-nowrap"
          style={{
            // Fläche in der Füllfarbe, Schrift in der Textfarbe (Kontrast, siehe AMPEL_TEXTFARBE)
            background: AMPEL_FARBE[e.ampel]
              ? `color-mix(in srgb, ${AMPEL_FARBE[e.ampel]} 15%, transparent)`
              : "var(--bg-hover)",
            color: AMPEL_TEXTFARBE[e.ampel] ?? "var(--text-muted)",
          }}
        >
          {ampelPilleText(e.ampel, e.quote)}
        </span>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr style={{ color: "var(--text-muted)" }}>
            <th className="text-left font-medium pb-1">Kennzahl</th>
            <th className="text-right font-medium pb-1">Ziel</th>
            <th className="text-right font-medium pb-1">Ist</th>
            <th className="text-right font-medium pb-1">Differenz</th>
          </tr>
        </thead>
        <tbody style={{ color: "var(--text-primary)" }}>
          <tr>
            <td className="py-1">
              <div className="flex items-center gap-1">
                Umsatz <KpiInfo schluessel="probezeitUmsatzGegenZiel" label="Umsatz gegen Ziel" />
              </div>
            </td>
            <td className="text-right tabular-nums">{euroText(e.zielUmsatz)}</td>
            <td className="text-right tabular-nums">{euroText(e.istUmsatz)}</td>
            <td
              className="text-right tabular-nums"
              style={{ color: umsatzDiffFarbe(umsatzDiff, e.ampel) ?? "inherit" }}
            >
              {diffText(umsatzDiff, "€")}
            </td>
          </tr>
          <tr>
            <td className="py-1">
              <div className="flex items-center gap-1">
                Stunden{" "}
                <KpiInfo
                  schluessel="probezeitStunden"
                  label="Stunden"
                  zusatz={stufenwerte ? zusatzStunden(e.stufe, stufenwerte) : undefined}
                />
              </div>
            </td>
            <td className="text-right tabular-nums">{stundenText(e.zielStunden)}</td>
            <td className="text-right tabular-nums">{stundenText(e.istStunden)}</td>
            <td className="text-right tabular-nums">{diffText(stundenDiff, "h")}</td>
          </tr>
          <tr>
            <td className="py-1">
              <div className="flex items-center gap-1">
                Ø VK{" "}
                <KpiInfo
                  schluessel="probezeitVk"
                  label="Ø VK"
                  zusatz={stufenwerte ? zusatzVk(e.stufe, stufenwerte) : undefined}
                />
              </div>
            </td>
            <td className="text-right tabular-nums">{vkText(e.vkZiel)}</td>
            <td className="text-right tabular-nums">{vkText(e.vkIst)}</td>
            <td className="text-right tabular-nums">{diffText(vkDiff, "€/h")}</td>
          </tr>
        </tbody>
      </table>

      <p className="text-sm mt-3 flex items-center gap-1" style={{ color: "var(--text-primary)" }}>
        <span>{kostenzeileText(e)}</span>
        <KpiInfo
          schluessel="probezeitVollkosten"
          label="Vollkosten und DB"
          zusatz={stufenwerte ? zusatzVollkosten(e.stufe, stufenwerte) : undefined}
        />
      </p>
      <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
        {satzText(e.satz, e.restWochen, e.wochenGesamt)}
      </p>

      <WochenBalken reihe={e.reihe} gezaehlteWoche={e.woche} />
      {stand && (
        <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>
          {stand}
        </p>
      )}
    </div>
  );
}
